import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";

const { color, radius, typeScale } = adminTokens;
const buttonHeight = 32;
// A finger needs 44px in the phone layout (PHONE-R3, PHONE-R4).
const phoneButtonHeight = 44;
const copiedFor = 1500;

const StyledCopyButton = styled.button<{ $isPhone: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: ${({ $isPhone }) => ($isPhone ? phoneButtonHeight : buttonHeight)}px;
  min-width: ${({ $isPhone }) => ($isPhone ? phoneButtonHeight : buttonHeight)}px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: ${radius.pill}px;
  background: transparent;
  color: ${color.inkSoft};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${color.border};
    background: ${color.surface};
  }

  /* The admin's 3px teal focus outline (XCUT-R38). */
  &:focus-visible {
    ${focusOutline}
  }
`;

// Holds the Button and its status text, so the hidden text is placed beside the Button.
const Host = styled.span`
  position: relative;
  display: inline-flex;
  flex: none;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

// Whether the value reached the clipboard; a browser that refuses it leaves the page as it was.
const writeToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};

interface CopyButtonProps {
  // The whole value to copy, such as a full order ID or a Case's number (ACCT-R22).
  value: string;
  // What it copies, as the Button names it: "email", "phone", "Case ID", "order ID".
  what: string;
}

// An icon-only Button that copies a value, reads "Copied" for 1.5 seconds and tells a screen
// reader what was copied (ACCT-R21, ACCT-R22).
export const CopyButton = ({ value, what }: CopyButtonProps) => {
  const isPhone = useIsPhoneLayout();
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
      clearTimeout(timer.current);
    },
    []
  );

  // "Copied" shows only once the value is on the clipboard.
  const showCopied = () => {
    if (!isMounted.current) return;

    setIsCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setIsCopied(false), copiedFor);
  };

  const copy = async () => {
    if (await writeToClipboard(value)) showCopied();
  };

  return (
    <Host>
      <StyledCopyButton
        type="button"
        $isPhone={isPhone}
        aria-label={`Copy ${what}`}
        title={`Copy ${what}`}
        onClick={copy}
      >
        {isCopied ? "Copied" : <CopyIcon />}
      </StyledCopyButton>
      <VisuallyHidden role="status">{isCopied ? `${what} copied` : ""}</VisuallyHidden>
    </Host>
  );
};
